"use client";

import { Fragment } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import CTA from "@/app/(components)/specifics/pages/Home/components/CTA/CTA";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { HomeCtaAction } from "@/app/types/common/HomeCtaAction";
import { FounderOffer } from "@/app/vars/Credits";
import { Globals } from "@/app/vars/Globals";
import styles from "./Hero.module.css";

type HeroKeys = {
  supTitle: TKey;
  secondaryCta?: TKey;
  pricing?: TKey;
  title: TKey;
  text: TKey;
};

type Props = DefaultProps & {
  freeCredits?: number;
  keys: HeroKeys;
};

export default function Hero(props: Props) {
  const T = useT();
  const { params } = props;
  const renderMarked = (text: string, className: string) =>
    text.split("\n").map((line, lineIndex) => (
      <Fragment key={`line-${lineIndex}`}>
        {lineIndex > 0 && (
          <>
            <br className={styles.lineBreak} />{" "}
          </>
        )}
        {line.split("**").map((part, i) =>
          i % 2 === 1 ? (
            <span key={`${lineIndex}-${i}`} className={className}>
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </Fragment>
    ));
  const renderHighlighted = (text: string) =>
    renderMarked(text, `${styles.highlight} ${styles.titleHighlight}`);

  return (
    <div className={styles.container}>
      <div className={styles.glowBlob}></div>
      <div className={styles.content}>
        <div className={styles.mainWrapper}>
          <div className={styles.main}>
            <div className={styles.headline}>
              <div className={`${styles.badgeContainer} ${styles.animateSlideUp}`}>
                <div className={styles.badge}>
                  <span className={styles.dot}></span>
                  <span>{T(props.keys.supTitle)}</span>
                </div>
              </div>

              <h1 className={styles.title}>
                <span className={`${styles.titleLine} ${styles.animateSlideUp} ${styles.delay1}`}>
                  {renderHighlighted(T(props.keys.title))}
                </span>
              </h1>
            </div>

            <p className={`${styles.text} ${styles.animateSlideUp} ${styles.delay4}`}>
              {renderMarked(T(props.keys.text), styles.textAccent)}
            </p>

            <div className={`${styles.ctaBlock} ${styles.animateSlideUp} ${styles.delay5}`}>
              <div className={styles.ctaRow}>
                <div className={styles.ctaWrapper}>
                  <div className={styles.ctaGlow}></div>
                  <CTA
                    params={params}
                    hideIcon
                    min={false}
                    action={HomeCtaAction.DEMO}
                    ctaKey={props.keys.secondaryCta ?? TKey.HOME_CTA_BOOK_DEMO}
                  />
                </div>
                {!Globals.WAITLIST_ENABLED && (
                  <Button
                    text={T(TKey.HOME_CTA_START_FREE)}
                    type={ButtonType.OUTLINE}
                    isLink={false}
                    min={false}
                    onClick={() => ModalStore.getState().openSignUp(params)}
                  />
                )}
              </div>
              {Globals.WAITLIST_ENABLED && (
                <p className={styles.pricing}>
                  <IconCheck color="var(--color-green-2)" />
                  {T(TKey.HOME_WAITLIST_HERO_NOTE).replace(
                    "{{discount}}",
                    String(FounderOffer.DISCOUNT_PERCENT),
                  )}
                </p>
              )}
              {props.keys.pricing && !Globals.WAITLIST_ENABLED && (
                <p className={styles.pricing}>
                  <IconCheck color="var(--color-green-2)" />
                  {String(T(props.keys.pricing)).replace(
                    "{{credits}}",
                    String(props.freeCredits ?? 0),
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
